import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { cn } from '@/lib/utils';
import { fmtTime } from './format';

/**
 * Palette catégorielle (ordre fixe) : pas clair / pas sombre, validée sur la surface des cartes.
 */
export const SERIES_COLORS = [
    { stroke: 'stroke-[#2a78d6] dark:stroke-[#3987e5]', bg: 'bg-[#2a78d6] dark:bg-[#3987e5]' },
    { stroke: 'stroke-[#eb6834] dark:stroke-[#d95926]', bg: 'bg-[#eb6834] dark:bg-[#d95926]' },
    { stroke: 'stroke-[#1baf7a] dark:stroke-[#199e70]', bg: 'bg-[#1baf7a] dark:bg-[#199e70]' },
    { stroke: 'stroke-[#eda100] dark:stroke-[#c98500]', bg: 'bg-[#eda100] dark:bg-[#c98500]' },
    { stroke: 'stroke-[#e87ba4] dark:stroke-[#d55181]', bg: 'bg-[#e87ba4] dark:bg-[#d55181]' },
    { stroke: 'stroke-[#4a3aa7] dark:stroke-[#9085e9]', bg: 'bg-[#4a3aa7] dark:bg-[#9085e9]' },
];

const useElementWidth = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!ref.current) return;
        const obs = new ResizeObserver((entries) => {
            setWidth(Math.floor(entries[0].contentRect.width));
        });
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return { ref, width };
};


/**
 * Mini courbe pour les cartes (une seule série, pas d'axe).
 */
export function Sparkline({ values, className }: { values: number[]; className?: string }) {
    const { ref, width } = useElementWidth();
    const height = 32;
    const path = useMemo(() => {
        if (!width || values.length < 2) return null;
        const max = Math.max(...values, 0) || 1;
        const x = d3.scaleLinear().domain([0, values.length - 1]).range([1, width - 1]);
        const y = d3.scaleLinear().domain([0, max]).range([height - 2, 2]);
        return d3.line<number>().x((_, i) => x(i)).y((v) => y(v))(values);
    }, [values, width]);
    return (
        <div ref={ref} className={cn('h-8 w-full', className)}>
            {path && (
                <svg width={width} height={height} className="block overflow-visible">
                    <path d={path} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
                        className="stroke-[#2a78d6] dark:stroke-[#3987e5]" />
                </svg>
            )}
        </div>
    );
}


export type ActivitySeries<T> = {
    key: keyof T & string;
    label: string;
    fmt: (v: number) => string;
};

/**
 * Graphique d'activité multi-séries. Les séries ont des unités différentes : chacune est
 * indexée sur son propre pic (une seule échelle 0–100 %), la valeur réelle est dans l'infobulle.
 */
export function ActivityChart<T extends { t: number }>({ data, series, height = 230, withDate = false }: {
    data: T[];
    series: ActivitySeries<T>[];
    height?: number;
    withDate?: boolean;
}) {
    const { ref, width } = useElementWidth();
    const [hidden, setHidden] = useState<Set<string>>(new Set());
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);
    const margin = { top: 10, right: 12, bottom: 22, left: 36 };
    const innerW = Math.max(0, width - margin.left - margin.right);
    const innerH = height - margin.top - margin.bottom;

    const scales = useMemo(() => {
        if (data.length < 2 || !innerW) return null;
        const x = d3.scaleTime()
            .domain([new Date(data[0].t), new Date(data[data.length - 1].t)])
            .range([0, innerW]);
        const y = d3.scaleLinear().domain([0, 1]).range([innerH, 0]);
        const peaks: Record<string, number> = {};
        for (const s of series) {
            peaks[s.key] = Math.max(...data.map((d) => Number(d[s.key]) || 0), 0);
        }
        const paths = series.map((s) => {
            const peak = peaks[s.key] || 1;
            return d3.line<T>()
                .x((d) => x(new Date(d.t)))
                .y((d) => y((Number(d[s.key]) || 0) / peak))
                .curve(d3.curveMonotoneX)(data);
        });
        return { x, y, peaks, paths };
    }, [data, series, innerW, innerH]);

    const onMove = (e: React.MouseEvent<SVGRectElement>) => {
        if (!scales) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const t = scales.x.invert(e.clientX - rect.left).getTime();
        const idx = d3.bisector((d: T) => d.t).center(data, t);
        setHoverIdx(idx);
    };

    const toggle = (key: string) => {
        const next = new Set(hidden);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        setHidden(next);
    };

    const hoverPoint = hoverIdx !== null ? data[hoverIdx] : null;
    const hoverX = hoverPoint && scales ? scales.x(new Date(hoverPoint.t)) : 0;
    const tooltipLeft = hoverX + margin.left > width / 2;

    return (
        <div className="w-full">
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                {series.map((s, i) => (
                    <button
                        key={s.key}
                        type="button"
                        onClick={() => toggle(s.key)}
                        className={cn(
                            'flex items-center gap-1.5 text-muted-foreground hover:text-foreground',
                            hidden.has(s.key) && 'opacity-40',
                        )}
                        title={hidden.has(s.key) ? 'Show series' : 'Hide series'}
                    >
                        <span className={cn('inline-block h-0.5 w-4 rounded', SERIES_COLORS[i % SERIES_COLORS.length].bg)} />
                        {s.label}
                    </button>
                ))}
                <span className="ml-auto text-muted-foreground/70">each series scaled to its own peak</span>
            </div>
            <div ref={ref} className="relative w-full" style={{ height }}>
                {!scales ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                        Waiting for data…
                    </div>
                ) : (
                    <svg width={width} height={height} className="block">
                        <g transform={`translate(${margin.left},${margin.top})`}>
                            {[0, 0.25, 0.5, 0.75, 1].map((v) => (
                                <g key={v}>
                                    <line x1={0} x2={innerW} y1={scales.y(v)} y2={scales.y(v)}
                                        className="stroke-border" strokeWidth={1} />
                                    <text x={-6} y={scales.y(v)} dy="0.32em" textAnchor="end"
                                        className="fill-muted-foreground text-[10px]">{v * 100}%</text>
                                </g>
                            ))}
                            {scales.x.ticks(Math.max(2, Math.floor(innerW / 110))).map((tick) => (
                                <text key={tick.getTime()} x={scales.x(tick)} y={innerH + 15} textAnchor="middle"
                                    className="fill-muted-foreground text-[10px]">
                                    {fmtTime(tick.getTime(), withDate)}
                                </text>
                            ))}
                            {series.map((s, i) => !hidden.has(s.key) && scales.paths[i] && (
                                <path key={s.key} d={scales.paths[i]!} fill="none" strokeWidth={2}
                                    strokeLinejoin="round" strokeLinecap="round"
                                    className={SERIES_COLORS[i % SERIES_COLORS.length].stroke} />
                            ))}
                            {hoverPoint && (
                                <g>
                                    <line x1={hoverX} x2={hoverX} y1={0} y2={innerH}
                                        className="stroke-muted-foreground" strokeWidth={1} strokeDasharray="3 3" />
                                    {series.map((s, i) => !hidden.has(s.key) && (
                                        <circle key={s.key} cx={hoverX}
                                            cy={scales.y((Number(hoverPoint[s.key]) || 0) / (scales.peaks[s.key] || 1))}
                                            r={4} strokeWidth={2}
                                            className={cn('fill-card', SERIES_COLORS[i % SERIES_COLORS.length].stroke)} />
                                    ))}
                                </g>
                            )}
                            <rect width={innerW} height={innerH} fill="transparent"
                                onMouseMove={onMove} onMouseLeave={() => setHoverIdx(null)} />
                        </g>
                    </svg>
                )}
                {hoverPoint && scales && (
                    <div
                        className="pointer-events-none absolute top-2 z-10 min-w-48 rounded-md border bg-popover px-3 py-2 text-xs shadow-md"
                        style={tooltipLeft
                            ? { right: width - (hoverX + margin.left) + 12 }
                            : { left: hoverX + margin.left + 12 }}
                    >
                        <div className="mb-1 font-medium">{fmtTime(hoverPoint.t, withDate)}</div>
                        {series.map((s, i) => (
                            <div key={s.key} className={cn('flex items-center gap-2', hidden.has(s.key) && 'opacity-40')}>
                                <span className={cn('inline-block h-0.5 w-3 rounded', SERIES_COLORS[i % SERIES_COLORS.length].bg)} />
                                <span className="text-muted-foreground">{s.label}</span>
                                <span className="ml-auto pl-3 font-mono">{s.fmt(Number(hoverPoint[s.key]) || 0)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
